import { isTAFEDate, getTAFEBlockForDate } from '../utils/mockDataLeave'
// src/pages/Roster.tsx — Crew Roster & Resource Allocation
// PM and PC can allocate employees and equipment to jobs per day
// Conflict detection prevents double-booking across jobs
// Allocations auto-generate timesheet entries for payroll

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import {
  MOCK_ALLOCATIONS, MOCK_EQUIPMENT_ALLOCATIONS,
  WEEK_DAYS, PREV_WEEK_DAYS, DAY_LABELS,
  getEmployeeWeekAvailability, checkAllocationConflict,
  checkEquipmentConflict, generatePayrollSummary,
  JOB_ALLOC_COLOUR, DayAllocation, EquipmentDayAllocation,
} from '../utils/mockDataRoster'
import { MOCK_EMPLOYEES, MOCK_EQUIPMENT, PAY_RATES,
  getOpenEquipmentDefects, getEmployeeInductionStatus, getEmployeeFMStatus,
  checkMinRestPeriod, getExpiredCredentials, getExpiringCredentials,
  SUPERVISOR_CREDENTIAL_REQUIREMENTS, calcApprenticeYear,
  getWeeklyAllocatedHours, getWeeklyHoursCap, addRosterChangeEntry,
  getRosterChangeLog, RosterChangeEntry,
  getOTThreshold,
} from '../utils/mockDataExtended'
import { MOCK_CONFIGURED_TASKS } from '../utils/mockDataMobile'
import { getEffectiveBOMStockStatus } from '../utils/mockDataGRN'
import { MOCK_JOBS, PROJECT_TYPE_META } from '../utils/mockData'
import { formatCurrency, formatDate } from '../utils/formatters'
import {
  Users, Truck, ChevronLeft, ChevronRight,
  AlertTriangle, FileText, Plus, X, DollarSign, Download, Zap, Info, Moon, Sun, RefreshCw, Send} from 'lucide-react'

type Employee = (typeof MOCK_EMPLOYEES)[number]

// ── Helpers ────────────────────────────────────────────────────────
function datePlusDays(base: string, n: number): string {
  const d = new Date(base); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10)
}

// HP5: Dynamic current-week anchoring — replaces hardcoded 2026-06-15 seed dates.
// weekOffset=0 now always represents the real current week.
// Seed data allocations (mid-June 2026) will appear at negative offsets when
// the platform is used with live data beyond that date.
function _getThisMonday(): string {
  const now = new Date()
  const day = now.getDay()              // 0=Sun, 1=Mon … 6=Sat
  const diff = day === 0 ? -6 : 1 - day // days back to Monday
  const d = new Date(now)
  d.setDate(now.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}
const BASE_MONDAY = _getThisMonday()
const BASE_SUNDAY = (() => {
  const d = new Date(BASE_MONDAY + 'T00:00:00')
  d.setDate(d.getDate() - 1)            // Sunday before Monday = night shift week anchor
  return d.toISOString().slice(0, 10)
})()

function parseWeekOffset(offsetWeeks: number, view: 'DAY'|'NIGHT'='DAY'): string[] {
  const anchor = view === 'NIGHT' ? BASE_SUNDAY : BASE_MONDAY
  const start  = datePlusDays(anchor, offsetWeeks * 7)
  return Array.from({ length: 7 }, (_, i) => datePlusDays(start, i))
}

function formatShortDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function summariseEmployeeCredentials(credentials: Employee['credentials']) {
  return [
    credentials.electricalLicenceNumber ? 'Electrical' : null,
    credentials.firstAidExpiry ? 'First Aid' : null,
    credentials.whsInductionExpiry ? 'WHS' : null,
    credentials.tcaExpiry ? 'TCA' : null,
    credentials.workingAtHeights ? 'Heights' : null,
    credentials.confinedSpaceEntry ? 'Confined Space' : null,
    credentials.hvAuthority ? 'HV' : null,
  ].filter(Boolean) as string[]
}



// ── Day status for roster entries ────────────────────────────────
type DayStatus = 'WORKING' | 'SICK' | 'ANNUAL_LEAVE' | 'RDO' | 'TOIL' | 'WORKERS_COMP' | 'TAFE_ATTENDANCE' | 'OTHER'

interface AllocationOverride {
  allocationId: string | null  // null = date with no job alloc but a status override
  employeeId: string
  date: string
  dayStatus: DayStatus
  adjustedHours: number | null  // null = default 8h assumed
  statusNote: string | null     // required when dayStatus === 'OTHER'
  updatedByName: string
  updatedAt: string
}

// Module-level store for overrides — shared between Roster and MobileComms
// (replaces a full Zustand store for this prototype)
const _rosterOverrides: AllocationOverride[] = []

export type RosterPushEntry = {
  id: string; employeeId: string; employeeName: string
  jobId: string; jobNumber: string; date: string; shiftType: string
  pushedAt: string; pushedByName: string
  status: 'PENDING' | 'CONFIRMED' | 'REJECTED'
  confirmedAt: string | null
  tasks: Array<{ taskId: string; title: string; specClauseRef: string | null; holdPointRef: string | null; discipline: string }>
  materials: Array<{ bomLineId: string; description: string; qty: number; unit: string; stockStatus: string }>
  customNote: string | null
}

const _rosterPushLog: RosterPushEntry[] = []

export function getRosterOverrides() { return [..._rosterOverrides] }

// ─── Shared allocations store (Phase 3/4 — keeps MobileComms and Timesheets in sync) ───
// Shared roster allocation store — initialised from seed data so ResourceMatrix
// and other consumers have data even before the Roster component mounts.
let _rosterAllocations: DayAllocation[] = [...MOCK_ALLOCATIONS]

export function getRosterAllocations(): DayAllocation[] {
  return [..._rosterAllocations]
}
export function setRosterAllocations(allocs: DayAllocation[]): void {
  _rosterAllocations = [...allocs]
}
export function getRosterPushLog():   RosterPushEntry[] { return [..._rosterPushLog] }

export function upsertRosterOverride(o: AllocationOverride) {
  const idx = _rosterOverrides.findIndex(x => x.employeeId === o.employeeId && x.date === o.date)
  if (idx >= 0) _rosterOverrides[idx] = o
  else _rosterOverrides.push(o)
}

export function addRosterPush(entries: RosterPushEntry[]) {
  _rosterPushLog.push(...entries)
}

export function confirmRosterPush(id: string) {
  const idx = _rosterPushLog.findIndex(e => e.id === id)
  if (idx >= 0) _rosterPushLog[idx] = { ..._rosterPushLog[idx], status: 'CONFIRMED', confirmedAt: new Date().toISOString() }
}

export function rejectRosterPush(id: string) {
  const idx = _rosterPushLog.findIndex(e => e.id === id)
  if (idx >= 0) _rosterPushLog[idx] = { ..._rosterPushLog[idx], status: 'REJECTED' }
}

export function rollbackRosterPush(id: string, rolledBackByName: string, reason: string): void {
  const entry = _rosterPushLog.find(e => e.id === id)
  if (entry) {
    (entry as any).status           = 'ROLLED_BACK'
    ;(entry as any).rolledBackAt    = new Date().toISOString()
    ;(entry as any).rolledBackByName = rolledBackByName
    ;(entry as any).rollbackReason  = reason
  }
}

const DAY_STATUS_META: Record<DayStatus, { label: string; bg: string; text: string; shortLabel: string }> = {
  WORKING:        { label: 'Working',           bg: '',                   text: '',                shortLabel: ''      },
  SICK:           { label: 'Sick leave',         bg: 'bg-red-50',          text: 'text-red-700',    shortLabel: 'SICK'  },
  ANNUAL_LEAVE:   { label: 'Annual leave',       bg: 'bg-blue-50',         text: 'text-blue-700',   shortLabel: 'AL'    },
  RDO:            { label: 'RDO',                bg: 'bg-gray-100',        text: 'text-gray-500',   shortLabel: 'RDO'   },
  TOIL:           { label: 'TOIL',               bg: 'bg-teal-50',         text: 'text-teal-700',   shortLabel: 'TOIL'  },
  WORKERS_COMP:   { label: "Workers' comp",      bg: 'bg-amber-50',        text: 'text-amber-700',  shortLabel: 'WC'    },
  TAFE_ATTENDANCE:{ label: 'TAFE attendance',    bg: 'bg-yellow-50',       text: 'text-yellow-700', shortLabel: 'TAFE'  },
  OTHER:          { label: 'Other / note',       bg: 'bg-amber-50',        text: 'text-amber-700',  shortLabel: '⋯' },
}


// ── Status edit form — inside modal ──────────────────────────────
function StatusEditForm({
  existing, onSave, onCancel,
}: {
  existing?: AllocationOverride
  onSave: (status: DayStatus, hours: number | null, note: string | null) => void
  onCancel: () => void
}) {
  const [status, setStatus] = useState<DayStatus>(existing?.dayStatus ?? 'WORKING')
  const [hours, setHours]   = useState<string>(existing?.adjustedHours?.toString() ?? '')
  const [note, setNote]     = useState(existing?.statusNote ?? '')

  const hoursNum = hours === '' ? null : parseFloat(hours)
  const canSave  = status !== 'OTHER' || note.trim().length > 0

  return (
    <div className="p-5 space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">Day status</label>
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(DAY_STATUS_META) as DayStatus[]).map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors text-left ${
                status === s
                  ? s === 'SICK'         ? 'bg-red-100 border-red-400 text-red-800'
                  : s === 'ANNUAL_LEAVE' ? 'bg-blue-100 border-blue-400 text-blue-800'
                  : s === 'RDO'          ? 'bg-gray-200 border-gray-400 text-gray-700'
                  : s === 'OTHER'        ? 'bg-amber-100 border-amber-400 text-amber-800'
                  : 'bg-green-100 border-green-400 text-green-800'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}>
              {DAY_STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Adjusted hours <span className="text-gray-400 font-normal">(leave blank = 8h assumed for WORKING)</span>
        </label>
        <input type="number" min="0" max="16" step="0.5"
          value={hours} onChange={e => setHours(e.target.value)}
          placeholder="8.0"
          className="input text-sm w-full" />
      </div>

      {(status === 'OTHER' || status === 'SICK' || note) && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Notes {status === 'OTHER' && <span className="text-red-500">*</span>}
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            rows={3} placeholder={status === 'OTHER' ? 'Required — describe the other reason…' : 'Optional note…'}
            className="input text-sm w-full resize-none" />
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button onClick={onCancel} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
        <button onClick={() => onSave(status, hoursNum, note.trim() || null)}
          disabled={!canSave}
          className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
          Save
        </button>
      </div>
    </div>
  )
}

const NIGHT_ALLOCATIONS_SEED: DayAllocation[] = [
  {id:'nl001',employeeId:'e3',jobId:'j4',date:'2026-06-14',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-12T09:00:00Z',timesheetId:null,notes:'Night crew — Signal Loop Works'},
  {id:'nl002',employeeId:'e3',jobId:'j4',date:'2026-06-15',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-12T09:00:00Z',timesheetId:null,notes:null},
  {id:'nl003',employeeId:'e3',jobId:'j4',date:'2026-06-16',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-12T09:00:00Z',timesheetId:null,notes:null},
  {id:'nl004',employeeId:'e3',jobId:'j4',date:'2026-06-17',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-12T09:00:00Z',timesheetId:null,notes:null},
  {id:'nl005',employeeId:'e3',jobId:'j4',date:'2026-06-18',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-12T09:00:00Z',timesheetId:null,notes:'Last night — rotate to DAY next week'},
  {id:'nl006',employeeId:'e4',jobId:'j2',date:'2026-06-14',shiftType:'NIGHT',allocatedById:'u4',allocatedByName:'Sarah Lee',allocatedAt:'2026-06-12T09:10:00Z',timesheetId:null,notes:'Night TMR works — lane closures'},
  {id:'nl007',employeeId:'e4',jobId:'j2',date:'2026-06-15',shiftType:'NIGHT',allocatedById:'u4',allocatedByName:'Sarah Lee',allocatedAt:'2026-06-12T09:10:00Z',timesheetId:null,notes:null},
  {id:'nl008',employeeId:'e4',jobId:'j2',date:'2026-06-16',shiftType:'NIGHT',allocatedById:'u4',allocatedByName:'Sarah Lee',allocatedAt:'2026-06-12T09:10:00Z',timesheetId:null,notes:null},
  {id:'nl009',employeeId:'e4',jobId:'j2',date:'2026-06-17',shiftType:'NIGHT',allocatedById:'u4',allocatedByName:'Sarah Lee',allocatedAt:'2026-06-12T09:10:00Z',timesheetId:null,notes:null},
  {id:'nl010',employeeId:'e4',jobId:'j2',date:'2026-06-18',shiftType:'NIGHT',allocatedById:'u4',allocatedByName:'Sarah Lee',allocatedAt:'2026-06-12T09:10:00Z',timesheetId:null,notes:null},
  {id:'nl011',employeeId:'e6',jobId:'j3',date:'2026-06-14',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-15T10:00:00Z',timesheetId:null,notes:'Broadbeach night works'},
  {id:'nl012',employeeId:'e6',jobId:'j3',date:'2026-06-15',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-15T10:00:00Z',timesheetId:null,notes:null},
  {id:'nl013',employeeId:'e6',jobId:'j3',date:'2026-06-16',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-15T10:00:00Z',timesheetId:null,notes:null},
  {id:'nl014',employeeId:'e6',jobId:'j3',date:'2026-06-17',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-15T10:00:00Z',timesheetId:null,notes:null},
  {id:'nl015',employeeId:'e6',jobId:'j3',date:'2026-06-18',shiftType:'NIGHT',allocatedById:'u5',allocatedByName:'Mike Walsh',allocatedAt:'2026-06-15T10:00:00Z',timesheetId:null,notes:null},
]
const INIT_SHIFT_PREFS: Record<string,'DAY'|'NIGHT'> = { 'e3':'NIGHT','e4':'NIGHT','e6':'NIGHT' }

// ── Alloc cell ─────────────────────────────────────────────────────
function AllocCell({
  employeeId, date, allocations, canEdit, onAllocate, onRemove, scopedJobIds, shiftFilter,
  override, onEditStatus,
}: {
  employeeId: string; date: string
  allocations: DayAllocation[]; canEdit: boolean
  onAllocate: (employeeId: string, date: string) => void
  onRemove: (allocationId: string) => void
  scopedJobIds: string[] | null
  shiftFilter: 'DAY' | 'NIGHT' | 'BOTH'
  override?: AllocationOverride
  onEditStatus?: (employeeId: string, date: string, allocationId: string | null) => void
}) {
  const statusMeta = override && override.dayStatus !== 'WORKING' ? DAY_STATUS_META[override.dayStatus] : null
  // Filter allocation by shift type — in DAY view show only DAY allocs,
  // NIGHT view only NIGHT allocs, BOTH shows whichever exists
  const alloc = allocations.find(a =>
    a.employeeId === employeeId && a.date === date &&
    (shiftFilter === 'BOTH' || a.shiftType === shiftFilter))

  if (alloc) {
    const c = JOB_ALLOC_COLOUR[alloc.jobId] ?? JOB_ALLOC_COLOUR.j1
    const job = MOCK_JOBS.find(j => j.id === alloc.jobId)
    // If PC doesn't own this job, show read-only badge
    const isOwned = scopedJobIds === null || scopedJobIds.includes(alloc.jobId)
    // Status override visual
    if (statusMeta) {
      return (
        <td className={`border border-gray-200 p-0 min-w-[7rem]`}>
          <div className={`h-10 px-2 flex items-center justify-between gap-1 group ${statusMeta.bg}`}>
            <div className="flex items-center gap-1 min-w-0">
              <span className={`text-xs font-bold ${statusMeta.text}`}>{statusMeta.shortLabel || statusMeta.label}</span>
              {override?.adjustedHours !== null && override?.adjustedHours !== undefined &&
                <span className="text-xs text-gray-400">{override.adjustedHours}h</span>}
              {override?.statusNote && <span className="text-xs text-gray-400 truncate max-w-[4rem]" title={override.statusNote}>⋯</span>}
            </div>
            {canEdit && isOwned && (
              <button onClick={() => onEditStatus && onEditStatus(employeeId, date, alloc?.id ?? null)}
                title="Edit status" className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/50 rounded">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
            )}
          </div>
        </td>
      )
    }

    return (
      <td className={`border border-gray-200 p-0 min-w-[7rem]`}>
        <div className={`h-10 px-2 flex items-center justify-between gap-1 group ${c.bg} ${isOwned ? '' : 'opacity-70'}`}>
          <div className="flex items-center gap-1 min-w-0">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
            <span className={`text-xs font-semibold truncate ${c.text}`}>{job?.jobNumber ?? alloc.jobId}</span>
            {alloc.shiftType === 'NIGHT' && <Zap className="w-3 h-3 flex-shrink-0 text-amber-500" />}
          </div>
          {canEdit && isOwned && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => onEditStatus && onEditStatus(alloc.employeeId, alloc.date, alloc.id)}
                title="Edit status / hours" className="p-0.5 hover:bg-white/50 rounded">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
              <button onClick={() => onRemove(alloc.id)} title="Remove allocation"
                className="p-0.5 hover:bg-white/50 rounded">
                <X className="w-3 h-3 text-gray-500" />
              </button>
            </div>
          )}
        </div>
      </td>
    )
  }

  // Empty cell — weekend columns get a subtly different background
  const dow = new Date(date + 'T00:00:00').getDay()  // 0=Sun, 6=Sat
  const isWeekend = dow === 6  // Saturday — typically non-working
  const isNightOff = shiftFilter === 'DAY' && dow === 0   // Sunday off for day crew
  const isDayOff   = shiftFilter === 'NIGHT' && (dow === 5 || dow === 6)  // Fri/Sat off for night crew
  const offShift = isWeekend || isNightOff || isDayOff

  return (
    <td className={`border border-gray-100 p-0 min-w-[7rem] ${offShift ? 'bg-gray-50' : ''}`}>
      {canEdit && !offShift
        ? (
            <button
              onClick={() => onAllocate(employeeId, date)}
              className="h-10 w-full flex items-center justify-center text-gray-300 hover:bg-blue-50 hover:text-blue-400 transition-colors group">
              <Plus className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
            </button>
          )
        : <div className={`h-10 ${offShift ? 'bg-gray-50/80' : 'bg-gray-50/50'}`} />
      }
    </td>
  )
}

// ── Allocate modal ─────────────────────────────────────────────────
function AllocateModal({
  employeeId, date, allocations, scopedJobIds, currentUser,
  onSave, onClose,
}: {
  employeeId: string; date: string
  allocations: DayAllocation[]; scopedJobIds: string[] | null
  currentUser: { id: string; fullName: string }
  onSave: (alloc: Omit<DayAllocation, 'id'>) => void
  onClose: () => void
}) {
  const emp           = MOCK_EMPLOYEES.find(e => e.id === employeeId) as any
  const availableJobs = (scopedJobIds === null
    ? MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
    : MOCK_JOBS.filter(j => scopedJobIds.includes(j.id) && !['COMPLETED','CLOSED'].includes(j.status)))

  const [selectedJob,  setSelectedJob]  = useState(availableJobs[0]?.id ?? '')
  const [shiftType,    setShiftType]    = useState<'DAY'|'NIGHT'|'HALF'>('DAY')
  const [notes,        setNotes]        = useState('')
  const [override,     setOverride]     = useState(false)   // PM override for warnings

  // ── Phase 1 checks ────────────────────────────────────────────
  const wcBlocked    = (emp as any)?.workerCompActive === true
  const tafeDayBlock = isTAFEDate(employeeId, date)

  // ── Phase 3 checks ────────────────────────────────────────────
  // E1: Site induction gate
  const inductionStatus = selectedJob ? getEmployeeInductionStatus(employeeId, selectedJob) : 'NOT_INDUCTED'
  const inductionWarn   = selectedJob && inductionStatus === 'NOT_INDUCTED'
  const inductionExpired= selectedJob && inductionStatus === 'EXPIRED'

  // E2: Credential expiry
  const expiredCreds   = emp ? getExpiredCredentials(emp.credentials ?? {}) : []
  const expiringCreds  = emp ? getExpiringCredentials(emp.credentials ?? {}) : []
  const hasExpiredCred = expiredCreds.length > 0
  const isElectrical   = emp?.trade === 'ELECTRICAL' && !emp?.credentials?.electricalLicenceNumber

  // F1: Minimum rest period
  const restCheck = checkMinRestPeriod(employeeId, date, shiftType, allocations)

  // F2: FM status
  const fmStatus = getEmployeeFMStatus(employeeId, allocations)

  // F3: Weekly hours cap check
  const weekDatesFromAllocs = [...new Set(allocations.map(a => a.date))].sort()
  const currentWeekHrs = getWeeklyAllocatedHours(employeeId, weekDatesFromAllocs, allocations)
  const weekCapHrs     = getWeeklyHoursCap()
  const proposedShiftHrs = shiftType === 'NIGHT' ? 9 : shiftType === 'HALF' ? 4 : 8
  const projectedWeekHrs  = currentWeekHrs + proposedShiftHrs
  const capWarn  = projectedWeekHrs >= weekCapHrs * 0.8 && projectedWeekHrs < weekCapHrs
  const capBlock = projectedWeekHrs >= weekCapHrs

  // Conflict check
  const conflict = selectedJob && !wcBlocked && !tafeDayBlock
    ? checkAllocationConflict(employeeId, [date], undefined, allocations, shiftType)
    : { hasConflict: false }

  // AP7: Supervision ratio for apprentices
  const isApprentice    = emp?.employmentType === 'APPRENTICE' || emp?.employmentType === 'HOST_APPRENTICE'
  const apprenticeYear  = isApprentice && emp?.apprenticeCommencementDate
    ? calcApprenticeYear(emp.apprenticeCommencementDate) : null
  const sameDateAllocs  = allocations.filter(a => a.date === date && a.shiftType === shiftType)
  const licensedOnDay   = sameDateAllocs.filter(a => {
    const e = MOCK_EMPLOYEES.find(x => x.id === a.employeeId) as any
    return e?.credentials?.electricalLicenceNumber && e.employmentType !== 'APPRENTICE'
  })
  const apprenticesOnDay = sameDateAllocs.filter(a => {
    const e = MOCK_EMPLOYEES.find(x => x.id === a.employeeId) as any
    return e?.employmentType === 'APPRENTICE' || e?.employmentType === 'HOST_APPRENTICE'
  })
  // After this allocation, would the ratio exceed 3:1?
  const projectedApprentices = isApprentice ? apprenticesOnDay.length + 1 : apprenticesOnDay.length
  const ratioBreached = licensedOnDay.length > 0 && projectedApprentices > licensedOnDay.length * 3

  // AP5: Supervision block — apprentice with no licensed supervisor on same shift
  const hasNoSupervisor = isApprentice && licensedOnDay.length === 0

  // Hard blockers (prevent save)
  const hardBlocked = wcBlocked || tafeDayBlock || conflict.hasConflict ||
    (restCheck.status === 'BLOCK' && !override) ||
    (hasNoSupervisor && !override) ||
    (capBlock && !override)

  const handleSave = () => {
    if (hardBlocked) return
    onSave({
      employeeId, jobId: selectedJob, date, shiftType,
      allocatedById: currentUser.id, allocatedByName: currentUser.fullName,
      allocatedAt: new Date().toISOString(), timesheetId: null, notes: notes || null,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-4">
        <div className="px-6 py-4 border-b bg-blue-50 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Allocate to job</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {emp?.fullName} · {formatShortDate(date)} {new Date(date).toLocaleDateString('en-AU',{weekday:'long'})}
              {isApprentice && apprenticeYear && (
                <span className="ml-2 text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">
                  Yr {apprenticeYear} apprentice
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">

          {/* ── Hard blocks ───────────────────────────────────── */}
          {wcBlocked && (
            <div className="bg-amber-50 border border-amber-400 rounded-xl px-4 py-3 text-xs text-amber-900">
              <strong>Workers' comp — blocked.</strong> {emp?.fullName} has an active WC claim ({(emp as any)?.workerCompClaimRef ?? '—'}). No site allocation until cleared.
            </div>
          )}
          {tafeDayBlock && (
            <div className="bg-yellow-50 border border-yellow-400 rounded-xl px-4 py-3 text-xs text-yellow-900">
              <strong>TAFE attendance.</strong> {emp?.fullName} is on block release on {date}. A TAFE_ATTENDANCE timesheet entry will auto-generate. No site allocation on TAFE days.
            </div>
          )}
          {conflict.hasConflict && (
            <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs text-red-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div><strong>Shift conflict.</strong> {emp?.fullName} is already allocated to {(conflict as any).conflictJobNumber} on this date/shift. Resolve before allocating.</div>
            </div>
          )}
          {restCheck.status === 'BLOCK' && (
            <div className="bg-red-50 border border-red-400 rounded-xl px-4 py-3 text-xs text-red-900">
              <strong>F1 — Minimum rest violation.</strong> {restCheck.reason}
              <div className="mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} className="rounded" />
                  <span>PM override — I acknowledge the rest period concern and confirm this allocation is necessary</span>
                </label>
              </div>
            </div>
          )}
          {restCheck.status === 'WARN' && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-xs text-amber-800">
              <strong>F1 — Rest period advisory.</strong> {restCheck.reason}
            </div>
          )}
          {hasNoSupervisor && (
            <div className="bg-red-50 border border-red-400 rounded-xl px-4 py-3 text-xs text-red-900">
              <strong>AP5 — No licensed supervisor.</strong> {emp?.fullName} is an apprentice and requires a licensed electrical worker allocated to the same site and shift. No licensed worker is currently rostered for {formatShortDate(date)} {shiftType} shift.
              <div className="mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} className="rounded" />
                  <span>PM override — supervision will be arranged separately (document reason in notes)</span>
                </label>
              </div>
            </div>
          )}

          {/* ── FM warning ────────────────────────────────────── */}
          {fmStatus !== 'CLEAR' && (
            <div className={`border rounded-xl px-4 py-3 text-xs ${fmStatus === 'RED' ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
              <strong>FM {fmStatus === 'RED' ? '— mandatory rest required' : 'advisory'}.</strong> {emp?.fullName} has {fmStatus === 'RED' ? '6+' : '4+'} consecutive working days this week. {fmStatus === 'RED' ? 'A rest day is mandatory before further allocation.' : 'Consider scheduling a rest day soon.'}
            </div>
          )}

          {/* ── F3: Weekly hours cap ─────────────────────────── */}
          {(capWarn || capBlock) && (
            <div className={`border rounded-xl px-4 py-3 text-xs ${capBlock ? 'bg-red-50 border-red-400 text-red-900' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
              <strong>F3 — Weekly hours cap.</strong> {emp?.fullName} currently has {currentWeekHrs}h allocated this week. Adding this {shiftType} shift would bring the total to {projectedWeekHrs}h — {capBlock ? `exceeding the ${weekCapHrs}h cap` : `approaching the ${weekCapHrs}h cap (80% threshold)`}.
              {capBlock && (
                <div className="mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} className="rounded" />
                    <span>PM override — I acknowledge this exceeds the weekly hours cap and confirm this allocation is operationally necessary</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* ── E2: Credential warnings ───────────────────────── */}
          {hasExpiredCred && (
            <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs text-red-800">
              <strong>E2 — Expired credentials:</strong> {expiredCreds.join(' · ')}. Resolve before allocating to a live site.
            </div>
          )}
          {!hasExpiredCred && expiringCreds.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-xs text-amber-800">
              <strong>E2 — Credentials expiring soon:</strong> {expiringCreds.join(' · ')}
            </div>
          )}
          {isElectrical && (
            <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs text-red-800">
              <strong>E2 — No electrical licence on record.</strong> {emp?.fullName} is classified as ELECTRICAL trade but has no licence number recorded. Allocating to electrical work may breach the Electricity Safety Act 2002 (Qld).
            </div>
          )}

          {/* ── AP7: Supervision ratio ────────────────────────── */}
          {ratioBreached && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-xs text-amber-800">
              <strong>AP7 — Supervision ratio.</strong> Adding {emp?.fullName} would result in {projectedApprentices} apprentice{projectedApprentices !== 1 ? 's' : ''} supervised by {licensedOnDay.length} licensed worker{licensedOnDay.length !== 1 ? 's' : ''} on this shift ({projectedApprentices}:{licensedOnDay.length} ratio). EBA typically requires 3:1 maximum.
            </div>
          )}

          {/* ── Job selector ──────────────────────────────────── */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Job <span className="text-red-500">*</span></label>
            <select className="input" value={selectedJob} onChange={e => setSelectedJob(e.target.value)}>
              {availableJobs.length === 0
                ? <option>No active jobs</option>
                : availableJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber} — {j.description?.split('—')[0]?.trim() ?? j.jobNumber}</option>)
              }
            </select>
          </div>

          {/* E1: Induction gate */}
          {(inductionWarn || inductionExpired) && selectedJob && (
            <div className={`border rounded-xl px-4 py-3 text-xs ${inductionExpired ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
              <strong>E1 — {inductionExpired ? 'Site induction EXPIRED' : 'No site induction recorded'}.</strong>{' '}
              {emp?.fullName} does not have a current induction for {availableJobs.find(j=>j.id===selectedJob)?.jobNumber ?? selectedJob}.
              {inductionWarn && ' Proceed only if induction is planned for the mobilisation date. Record the induction in the Inductions module.'}
            </div>
          )}

          {/* ── Shift type ────────────────────────────────────── */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Shift type</label>
            <div className="flex gap-2">
              {(['DAY','NIGHT','HALF'] as const).map(s => (
                <button key={s} onClick={() => setShiftType(s)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${shiftType === s ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  {s === 'DAY' ? '☀ Day' : s === 'NIGHT' ? '🌙 Night' : '½ Half'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <input className="input" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={hasNoSupervisor ? 'Required: document supervision arrangement if overriding AP5' : 'Optional — tasks, equipment, instructions'} />
          </div>

          {/* Employee summary */}
          {emp && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-600">
              <div className="font-medium text-gray-900 mb-1">{emp.fullName} — {(emp.payClassification ?? '').replace(/_/g,' ')}</div>
              <div className="flex items-center gap-3 flex-wrap">
                <span>Trade: {emp.trade}</span>
                {fmStatus !== 'CLEAR' && (
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold ${fmStatus === 'RED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    FM {fmStatus}
                  </span>
                )}
                {isApprentice && <span className="bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full">Apprentice</span>}
                {emp.credentials?.hvAuthority && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">HV ✓</span>}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave}
            disabled={!selectedJob || hardBlocked}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
            Confirm allocation
          </button>
        </div>
      </div>
    </div>
  )
}


// ── Equipment alloc cell ───────────────────────────────────────────
function EquipAllocCell({
  equipmentId, date, allocations, canEdit, onAllocate, onRemove,
}: {
  equipmentId: string; date: string; allocations: EquipmentDayAllocation[]
  canEdit: boolean; onAllocate: (eqId: string, date: string) => void; onRemove: (id: string) => void
}) {
  const alloc       = allocations.find(a => a.equipmentId === equipmentId && a.date === date)
  // I2: Block equipment allocation if OPEN defect exists on this asset
  const openDefects = getOpenEquipmentDefects(equipmentId)
  const hasDefect   = openDefects.length > 0
  const defectLabel = hasDefect ? openDefects[0].description : ''

  if (alloc) {
    const c   = JOB_ALLOC_COLOUR[alloc.jobId] ?? JOB_ALLOC_COLOUR.j1
    const job = MOCK_JOBS.find(j => j.id === alloc.jobId)
    return (
      <td className="border border-gray-200 p-0 min-w-[7rem]">
        <div className={`h-10 px-2 flex items-center justify-between group ${c.bg}`}>
          <span className={`text-xs font-semibold ${c.text}`}>{job?.jobNumber ?? alloc.jobId}</span>
          {canEdit && (
            <button onClick={() => onRemove(alloc.id)}
              className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/50 rounded">
              <X className="w-3 h-3 text-gray-500" />
            </button>
          )}
        </div>
      </td>
    )
  }

  // I2: Defect block — grey cell, unclickable, tooltip shows defect
  if (hasDefect) {
    return (
      <td className="border border-red-100 p-0 min-w-[7rem] relative group" title={`DEFECT: ${defectLabel}`}>
        <div className="h-10 bg-red-50 flex items-center justify-center gap-1 cursor-not-allowed">
          <AlertTriangle className="w-3 h-3 text-red-400" />
          <span className="text-[10px] font-semibold text-red-500">DEFECT</span>
        </div>
        <div className="absolute bottom-full left-0 z-20 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap max-w-xs">
          Open defect — allocation blocked: {defectLabel}
        </div>
      </td>
    )
  }

  return (
    <td className="border border-gray-100 p-0 min-w-[7rem]">
      {canEdit
        ? <button onClick={() => onAllocate(equipmentId, date)}
            className="h-10 w-full flex items-center justify-center text-gray-300 hover:bg-blue-50 hover:text-blue-400 transition-colors group">
            <Plus className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
          </button>
        : <div className="h-10 bg-gray-50/50" />
      }
    </td>
  )
}

// ── Payroll summary tab ────────────────────────────────────────────
function PayrollTab({ weekDates, allocations }: { weekDates: string[]; allocations: DayAllocation[] }) {
  const empNameMap = Object.fromEntries(MOCK_EMPLOYEES.map(e => [e.id, e.fullName]))
  const lines = generatePayrollSummary(weekDates, allocations, empNameMap)

  // Group by employee
  const byEmployee = lines.reduce((acc, line) => {
    if (!acc[line.employeeId]) acc[line.employeeId] = []
    acc[line.employeeId].push(line)
    return acc
  }, {} as Record<string, typeof lines>)

  const totalDays = lines.reduce((s, l) => s + l.daysWorked, 0)
  const weekStart = weekDates[0]
  const weekEnd   = weekDates[weekDates.length - 1]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Payroll period: <strong>{formatDate(weekStart)}</strong> – <strong>{formatDate(weekEnd)}</strong>
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            Generated from confirmed allocations · {totalDays} employee-day{totalDays !== 1 ? 's' : ''} across {lines.length} job-employee combination{lines.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => generateRosterPDF(MOCK_EMPLOYEES, allocations, weekDates, MOCK_JOBS, weekDates[0] ?? "")} className="btn-secondary flex items-center gap-2 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            Print schedule
          </button>
          <button onClick={() => (() => {
            // O4: Generate from actual current roster allocations
            const jobsWithAllocs = [...new Set(allocations.map(a => a.jobId))]
            const firstJob = MOCK_JOBS.find(j => jobsWithAllocs.includes(j.id)) ?? MOCK_JOBS[0]
            const allocatedEmpIds = [...new Set(allocations.filter(a => a.jobId === firstJob.id).map(a => a.employeeId))]
            const allocatedEmps = MOCK_EMPLOYEES.filter(e => allocatedEmpIds.includes(e.id))
            generateMobilisationNotice(firstJob, allocatedEmps.length > 0 ? allocatedEmps : MOCK_EMPLOYEES, weekDates[0])
          })()} className="btn-secondary flex items-center gap-2 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
            Mobilisation notice
          </button>
          <button onClick={() => void 0 /* TODO: exportRosterCSV */} className="btn-secondary flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" />Export CSV
          </button>
        </div>
      </div>

      {Object.entries(byEmployee).map(([empId, empLines]) => {
        const emp = MOCK_EMPLOYEES.find(e => e.id === empId)
        const rate = PAY_RATES[emp?.payClassification as keyof typeof PAY_RATES]
        const totalDaysThisEmp = empLines.reduce((s, l) => s + l.daysWorked, 0)
        const estimatedCost = typeof rate === 'number' ? rate * 8 * totalDaysThisEmp : null

        return (
          <div key={empId} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <span className="font-semibold text-gray-900">{emp?.fullName ?? empId}</span>
                <span className="text-xs text-gray-500 ml-2">{emp?.payClassification?.replace(/_/g,' ')}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900">{totalDaysThisEmp} day{totalDaysThisEmp !== 1 ? 's' : ''}</div>
                {estimatedCost && <div className="text-xs text-gray-400">est. {formatCurrency(estimatedCost)}</div>}
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {empLines.map(line => {
                const meta = PROJECT_TYPE_META[MOCK_JOBS.find(j => j.id === line.jobId)?.type ?? 'SIGNALS']
                return (
                  <div key={`${empId}:${line.jobId}`} className="px-5 py-2.5 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${meta?.dot ?? 'bg-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 w-28">{line.jobNumber}</span>
                    <div className="flex gap-2 text-xs">
                      {line.shiftBreakdown.DAY   > 0 && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{line.shiftBreakdown.DAY} day shift{line.shiftBreakdown.DAY !== 1 ? 's' : ''}</span>}
                      {line.shiftBreakdown.NIGHT > 0 && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{line.shiftBreakdown.NIGHT} night shift{line.shiftBreakdown.NIGHT !== 1 ? 's' : ''}</span>}
                      {line.shiftBreakdown.HALF  > 0 && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{line.shiftBreakdown.HALF} half day</span>}
                    </div>
                    <div className="ml-auto text-xs text-gray-400">{line.daysWorked} day{line.daysWorked !== 1 ? 's' : ''}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {lines.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl py-14 text-center text-gray-400">
          <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No allocations recorded for this week</p>
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-500">
        <strong>Note:</strong> Estimated costs use base ordinary time rates (8 hrs/day). Actual payroll calculations include overtime, allowances, and leave loading per the applicable EBA. Export to payroll system for final processing.
      </div>
    </div>
  )
}

// ── MAIN PAGE ──────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// PHASE 4 — SITE MOBILISATION NOTICE
// Formal notice to field crew of job start: site address, reporting
// instructions, WHSO contact, site rules, PPE requirements.
// ─────────────────────────────────────────────────────────────────
function generateMobilisationNotice(job: any, employees: any[], mobilisationDate: string) {
  const now     = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const mobDate = mobilisationDate ? new Date(mobilisationDate).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : now

  const empList = employees.length > 0
    ? employees.map(e => `<li style="margin:3px 0">${e.fullName} — ${(e.payClassification ?? '').replace(/_/g,' ')}</li>`).join('')
    : '<li>All crew as rostered — see attached roster</li>'

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Site Mobilisation Notice — ${job?.jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:20mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:16px 0 8px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
.mob-banner{background:#1B3A6B;color:#fff;border-radius:6px;padding:14px 20px;margin:14px 0;text-align:center}
.info table{width:100%;border-collapse:collapse;margin-bottom:12px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
ul{margin:6px 0;padding-left:22px;line-height:1.8}
.ppe-box{background:#fef3c7;border:1.5px solid #fbbf24;border-radius:5px;padding:10px 16px;margin:12px 0}
.contact-box{background:#dcfce7;border:1.5px solid #86efac;border-radius:5px;padding:10px 16px;margin:12px 0}
.sig-row{display:flex;gap:40px;margin-top:18px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:30px;margin:16px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Issued: ${now}</div></div>
</div>

<div class="mob-banner">
  <div style="font-size:11pt;letter-spacing:2px;text-transform:uppercase;margin-bottom:6px">Site Mobilisation Notice</div>
  <div style="font-size:22pt;font-weight:900">${job?.jobNumber ?? '—'}</div>
  <div style="font-size:11pt;margin-top:4px">${job?.description ?? ''}</div>
</div>

<div class="info"><table>
  <tr><td>Client</td><td><strong>${job?.client ?? '—'}</strong></td></tr>
  <tr><td>Mobilisation date</td><td><strong>${mobDate}</strong> — REPORT AT 06:30 HRS</td></tr>
  <tr><td>Project start date</td><td>${job?.startDate ? new Date(job.startDate).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</td></tr>
  <tr><td>Estimated end date</td><td>${job?.targetEnd ? new Date(job.targetEnd).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</td></tr>
  <tr><td>Project type</td><td>${(job?.type ?? '').replace(/_/g,' ')}</td></tr>
  <tr><td>Crew size</td><td>${employees.length || job?.crew || '—'} personnel</td></tr>
</table></div>

<h2>Reporting instructions</h2>
<p>All crew are to report to the <strong>MCB Civil Electrical site compound</strong> on the mobilisation date and time stated above. Proceed directly to site — do NOT enter the work area before the WHSO/supervisor has conducted the site induction and toolbox talk.</p>
<ul>
  <li><strong>First report to:</strong> Site supervisor / WHSO on arrival</li>
  <li><strong>Reporting time:</strong> <strong>06:30 AEST</strong> — do not arrive earlier without prior approval</li>
  <li>Site induction will be conducted at first muster — do not commence work beforehand</li>
  <li>Sign on to the induction register before entering the work zone</li>
</ul>

<h2>Personnel mobilised</h2>
<ul>${empList}</ul>

<div class="ppe-box">
  <strong>⚠ MANDATORY PPE — must be worn at all times in the work zone:</strong>
  <ul style="margin-top:6px">
    <li>AS/NZS 4602 Class D/N high-visibility vest (minimum)</li>
    <li>Hard hat (Type 1 Class C) — mandatory on all sites</li>
    <li>Steel-capped safety boots (AS/NZS 2210.3)</li>
    <li>Safety glasses (AS/NZS 1337) — when directed</li>
    <li>Additional PPE as directed by WHSO and site-specific SWMS</li>
  </ul>
</div>

<h2>Site rules</h2>
<ul>
  <li>Zero tolerance: no drugs or alcohol on site — you must be fit for work</li>
  <li>Speed limit in compound and site access areas: 10 km/h</li>
  <li>All incidents, near misses, and hazards must be reported to the WHSO immediately</li>
  <li>No mobile phone use while operating plant or vehicles</li>
  <li>SWMS must be read and signed before commencing any high-risk work activity</li>
  <li>Do not enter the road work area without approved traffic control in place</li>
</ul>

<div class="contact-box">
  <strong>Key contacts</strong>
  <ul style="margin-top:6px;list-style:none;padding-left:0">
    <li>Project Manager: Jane Smith — 0412 345 678 (after hours emergency: 0412 345 678)</li>
    <li>WHSO / Site Supervisor: Sarah Kim — 0489 012 345</li>
    <li>Emergency services: 000</li>
    <li>Site address / muster point: refer to site establishment drawing</li>
  </ul>
</div>

<h2>Acknowledgement</h2>
<p style="font-size:10pt">By signing below, I confirm I have received and read this mobilisation notice and understand the reporting requirements, site rules, and PPE requirements. I will report any fitness-for-work concerns to the WHSO before commencing work.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Crew member name &amp; signature</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Crew member name &amp; signature</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager / WHSO — issued by</div></div>
</div>

<div class="footer">MCB Civil Electrical · Site Mobilisation Notice · ${job?.jobNumber ?? ''} · Issued ${now} · Retain for project records</div>
</body></html>`
  const win = window.open('', '_blank', 'width=960,height=820')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print notice'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}


function generateRosterPDF(
  employees: any[],
  allocations: any[],
  weekDates: string[],
  jobs: any[],
  weekLabel: string
) {
  const today    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const jobColor = (jobId: string | null) => {
    if (!jobId) return { bg: '#f9fafb', text: '#9ca3af' }
    const colors = ['#dbeafe','#dcfce7','#fef3c7','#f3e8ff','#ffe4e6','#e0f2fe','#fdf4ff','#f0fdf4']
    const jobIds = [...new Set(allocations.map(a => a.jobId).filter(Boolean))]
    const idx    = jobIds.indexOf(jobId)
    return { bg: colors[idx % colors.length] ?? '#f3f4f6', text: '#1f2937' }
  }

  const dayLabels  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
  const shortDate  = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })

  const headerCells = weekDates.map((d, i) =>
    `<th style="min-width:72px;padding:5px 4px;text-align:center;background:#1B3A6B;color:#fff;font-size:9pt">${dayLabels[i % 7]}<br><small style="font-weight:400">${shortDate(d)}</small></th>`
  ).join('')

  const empRows = employees.map(emp => {
    const cells = weekDates.map(date => {
      const alloc = allocations.find(a => a.employeeId === emp.id && a.date === date)
      if (!alloc) return '<td style="padding:5px 4px;text-align:center;background:#f9fafb;color:#d1d5db;font-size:9pt">—</td>'
      const job   = jobs.find(j => j.id === alloc.jobId)
      const { bg, text } = jobColor(alloc.jobId)
      const shift = alloc.shiftType === 'NIGHT' ? '🌙' : alloc.shiftType === 'HALF' ? '½' : ''
      return `<td style="padding:4px;text-align:center;background:${bg};color:${text};font-size:9pt;font-weight:600">${shift}${job?.jobNumber?.split('-')[2] ?? alloc.jobId}</td>`
    }).join('')
    return `<tr>
      <td style="padding:5px 10px;font-weight:bold;font-size:10pt;white-space:nowrap">${emp.fullName}</td>
      <td style="padding:5px 8px;font-size:9pt;color:#6b7280">${(emp.payClassification ?? '').replace(/_/g,' ')}</td>
      ${cells}
    </tr>`
  }).join('')

  // Daily crew count
  const countRow = weekDates.map(date => {
    const count = allocations.filter(a => a.date === date).length
    return `<td style="padding:4px;text-align:center;background:#1B3A6B;color:#fff;font-weight:bold;font-size:10pt">${count}</td>`
  }).join('')

  // Job colour legend
  const jobIds = [...new Set(allocations.map(a => a.jobId).filter(Boolean))]
  const legend = jobIds.map(jid => {
    const job = jobs.find(j => j.id === jid)
    const { bg, text } = jobColor(jid)
    return `<span style="display:inline-flex;align-items:center;gap:5px;margin-right:12px;font-size:9pt">
      <span style="width:18px;height:12px;background:${bg};border:1px solid #e5e7eb;border-radius:3px;display:inline-block"></span>
      ${job?.jobNumber ?? jid}${job?.description ? ' — '+job.description.slice(0,30) : ''}
    </span>`
  }).join('')

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Crew Deployment Schedule — ${weekLabel}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
table{width:100%;border-collapse:collapse}
th{text-align:left;padding:5px 10px;background:#1B3A6B;color:#fff}
td{border:0.5px solid #e5e7eb}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:17pt;font-weight:900;color:#1B3A6B">CREW DEPLOYMENT SCHEDULE</div>
    <div style="font-size:11pt;color:#1B3A6B">${weekLabel}</div>
    <div style="font-size:9pt;color:#888">Generated: ${today}</div></div>
</div>
<div style="margin-bottom:10px">${legend}</div>
<table>
  <thead><tr>
    <th style="min-width:140px">Employee</th>
    <th style="min-width:120px">Classification</th>
    ${headerCells}
  </tr></thead>
  <tbody>
    ${empRows}
    <tr style="border-top:2px solid #1B3A6B">
      <td colspan="2" style="padding:5px 10px;font-weight:bold;background:#1B3A6B;color:#fff">Crew on site</td>
      ${countRow}
    </tr>
  </tbody>
</table>
<p style="font-size:9pt;color:#6b7280;margin-top:10px">Job codes shown are the last component of the job number (e.g. 001 = TP-2026-001). 🌙 = night shift, ½ = half day.</p>
<div class="footer">MCB Civil Electrical · Crew Deployment Schedule · ${weekLabel} · Generated ${today} · Confidential — internal distribution</div>
</body></html>`

  const win = window.open('', '_blank', 'width=1100,height=750')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print schedule'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}

// ─────────────────────────────────────────────────────────────────
// PUSH DETAILS MODAL
// PM previews exactly what will be pushed (jobs, tasks, materials)
// and adds a custom note before sending to crew devices.
// ─────────────────────────────────────────────────────────────────
function PushDetailsModal({ entries, onConfirm, onClose }: {
  entries: Array<{ employeeName: string; jobNumber: string; date: string; shiftType: string; tasks: RosterPushEntry['tasks']; materials: RosterPushEntry['materials'] }>
  onConfirm: (note: string) => void
  onClose:   () => void
}) {
  const [note, setNote] = useState('')
  // Group by employee for display
  const byEmp: Record<string, typeof entries> = {}
  entries.forEach(e => { if (!byEmp[e.employeeName]) byEmp[e.employeeName] = []; byEmp[e.employeeName].push(e) })

  // Aggregate materials across all push entries (dedupe by bomLineId)
  const allMaterials = entries.flatMap(e => e.materials)
    .filter((m, i, a) => a.findIndex(x => x.bomLineId === m.bomLineId) === i)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto">

        {/* Header */}
        <div className="px-6 py-4 border-b bg-indigo-50 flex items-center justify-between sticky top-0">
          <div>
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Send className="w-4 h-4 text-indigo-600" /> Confirm roster push
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {entries.length} shift{entries.length !== 1 ? 's' : ''} across {Object.keys(byEmp).length} employee{Object.keys(byEmp).length !== 1 ? 's' : ''} will be notified
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* Per-employee summary */}
          <div className="space-y-3">
            {Object.entries(byEmp).map(([empName, empEntries]) => {
              const tasks = empEntries.flatMap(e => e.tasks).filter((t,i,a)=>a.findIndex(x=>x.taskId===t.taskId)===i)
              const mats  = empEntries.flatMap(e => e.materials).filter((m,i,a)=>a.findIndex(x=>x.bomLineId===m.bomLineId)===i)
              const jobs  = [...new Set(empEntries.map(e => e.jobNumber))]
              return (
                <div key={empName} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b bg-white flex items-center justify-between">
                    <div className="font-medium text-gray-900 text-sm">{empName}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{empEntries.length} shift{empEntries.length!==1?'s':''}</span>
                      <span>·</span>
                      <span>{jobs.join(', ')}</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    {/* Schedule */}
                    <div className="flex flex-wrap gap-1.5">
                      {empEntries.map(e => (
                        <span key={e.date} className={`text-xs px-2 py-0.5 rounded-full font-medium ${e.shiftType==='NIGHT'?'bg-indigo-100 text-indigo-700':'bg-sky-100 text-sky-700'}`}>
                          {new Date(e.date+'T00:00:00').toLocaleDateString('en-AU',{weekday:'short',day:'numeric',month:'short'})}
                          {e.shiftType==='NIGHT'?' 🌙':''}
                        </span>
                      ))}
                    </div>
                    {/* Tasks */}
                    {tasks.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-500 mb-1">Tasks assigned ({tasks.length})</div>
                        <div className="space-y-0.5">
                          {tasks.map(t => (
                            <div key={t.taskId} className="text-xs text-gray-700 flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                              {t.title}
                              {t.specClauseRef && <span className="text-gray-400">({t.specClauseRef})</span>}
                              {t.holdPointRef && <span className="text-orange-600 font-medium text-[10px]">HP: {t.holdPointRef}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Materials */}
                    {mats.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-500 mb-1">Materials to collect ({mats.length})</div>
                        <div className="flex flex-wrap gap-1">
                          {mats.map(m => (
                            <span key={m.bomLineId} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              m.stockStatus==='ON_SITE'?'bg-green-100 text-green-700':
                              m.stockStatus==='WAREHOUSE'?'bg-blue-100 text-blue-700':
                              'bg-amber-100 text-amber-700'}`}>
                              {m.description.slice(0,30)} — {m.qty}{m.unit}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {tasks.length === 0 && mats.length === 0 && (
                      <p className="text-xs text-gray-400 italic">No tasks or materials linked to these jobs yet — push will contain job + schedule only.</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Custom note */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Custom message to crew <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea className="input w-full resize-none h-16 text-sm" value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Report to site at 06:30 sharp. Crew bus leaves depot at 06:00. Bring your own lunch." />
          </div>

          {/* Materials summary */}
          {allMaterials.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-amber-800 mb-2">Materials crew must collect before starting ({allMaterials.length} items)</p>
              <div className="flex flex-wrap gap-1.5">
                {allMaterials.map(m => (
                  <span key={m.bomLineId} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    m.stockStatus==='ON_SITE'?'bg-green-100 text-green-700':
                    m.stockStatus==='WAREHOUSE'?'bg-blue-100 text-blue-700':'bg-amber-100 text-amber-700'}`}>
                    {m.description.slice(0,35)} · {m.stockStatus}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex gap-3 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary flex-shrink-0">Cancel</button>
          <button onClick={() => onConfirm(note.trim())}
            className="btn-primary flex-1 flex items-center justify-center gap-2">
            <Send className="w-4 h-4" /> Push {entries.length} shift{entries.length!==1?'s':''} to mobile
          </button>
        </div>
      </div>
    </div>
  )
}

export function Roster() {
  const { user, isTier, scopedJobIds } = useAuthStore()
  const [tab, setTab]               = useState<'crew'|'equipment'|'payroll'|'history'>('crew')
  const [weekOffset, setWeekOffset] = useState(0)
  const [weekSpan,   setWeekSpan]   = useState<1|2|3|4>(2)
  const [allocations, setAllocations] = useState<DayAllocation[]>(() => {
    const seed = [...MOCK_ALLOCATIONS, ...NIGHT_ALLOCATIONS_SEED]
    setRosterAllocations(seed)  // initialise shared store on first render
    return seed
  })
  const [eqAllocations, setEqAllocations] = useState<EquipmentDayAllocation[]>(MOCK_EQUIPMENT_ALLOCATIONS)
  const [allocModal, setAllocModal]   = useState<{ employeeId: string; date: string } | null>(null)
  const [eqModal, setEqModal]         = useState<{ equipmentId: string; date: string } | null>(null)
  const [shiftView, setShiftView]     = useState<'DAY'|'NIGHT'|'BOTH'>('DAY')
  const [shiftPrefs, setShiftPrefs]   = useState<Record<string,'DAY'|'NIGHT'>>(INIT_SHIFT_PREFS)
  const [rotateConfirm, setRotateConfirm] = useState<{id:string;name:string;from:'DAY'|'NIGHT'}|null>(null)
  const [overrides, setOverrides]           = useState<AllocationOverride[]>([])
  const [editStatusModal, setEditStatusModal] = useState<{ employeeId: string; date: string; allocationId: string | null } | null>(null)
  const [otherNoteModal, setOtherNoteModal]   = useState<{ employeeId: string; date: string } | null>(null)
  const [otherNoteText, setOtherNoteText]     = useState('')

  // Live roster allocation count from Neon
  // Compute date range from state (weekDates is declared later in function body)
  const _qFrom = datePlusDays(shiftView === 'NIGHT' ? BASE_SUNDAY : BASE_MONDAY, weekOffset * 7)
  const _qTo   = datePlusDays(_qFrom, weekSpan * 7 - 1)
  const { data: liveRosterRes } = useQuery({
    queryKey: ['roster-allocations', _qFrom, _qTo],
    queryFn:  async () => {
      const { data } = await api.get(`/api/roster/allocations?from=${_qFrom}&to=${_qTo}`)
      return data as any
    },
    staleTime: 30_000,
  })
  const liveAllocCount: number = (liveRosterRes as any)?.count ?? 0

  if (!user) return null

  const canEdit = isTier('B_PC')
  const jIds    = scopedJobIds()  // null = all, array = scoped

  // Week grid: DAY view = Mon-Sun (Mon first) | NIGHT view = Sun-Sat (Sun first)
  // DAY anchor: BASE_MONDAY (Mon 15 Jun) | NIGHT anchor: BASE_SUNDAY (Sun 14 Jun)
  function buildDateRange(offsetWeeks: number, numWeeks: number, view: 'DAY'|'NIGHT'|'BOTH'='DAY'): string[] {
    const anchor = view === 'NIGHT' ? BASE_SUNDAY : BASE_MONDAY
    const dates: string[] = []
    for (let w = 0; w < numWeeks; w++) {
      const wkStart = datePlusDays(anchor, (offsetWeeks + w) * 7)
      for (let d = 0; d < 7; d++) dates.push(datePlusDays(wkStart, d))
    }
    return dates
  }
  // Apply shift-view-aware anchor when generating date columns
  const weekDates = buildDateRange(weekOffset, weekSpan, shiftView === 'BOTH' ? 'DAY' : shiftView)
  const weekLabel = shiftView === 'NIGHT'
    ? `Night week: ${formatShortDate(weekDates[0])} (Sun) – ${formatShortDate(weekDates[6])} (Sat)`
    : weekSpan === 1
      ? `Week: ${formatShortDate(weekDates[0])} (Mon) – ${formatShortDate(weekDates[6])} (Sun)`
      : `${formatShortDate(weekDates[0])} – ${formatShortDate(weekDates[weekDates.length-1])}`
  function empShift(id: string): 'DAY'|'NIGHT' { return shiftPrefs[id] ?? 'DAY' }

  function getOverride(employeeId: string, date: string): AllocationOverride | undefined {
    return overrides.find(o => o.employeeId === employeeId && o.date === date)
  }

  function applyStatus(employeeId: string, empName: string, date: string, allocationId: string | null,
                        dayStatus: DayStatus, adjustedHours: number | null, statusNote: string | null) {
    const override: AllocationOverride = {
      allocationId, employeeId, date, dayStatus, adjustedHours, statusNote,
      updatedByName: user?.fullName ?? 'Unknown', updatedAt: new Date().toISOString(),
    }
    upsertRosterOverride(override)
    setOverrides(prev => {
      const idx = prev.findIndex(o => o.employeeId === employeeId && o.date === date)
      if (idx >= 0) { const next = [...prev]; next[idx] = override; return next }
      return [...prev, override]
    })
  }

  // Push modal state
  const [pushModal, setPushModal] = useState<typeof _pushPreview | null>(null)
  const _pushPreview: Array<{
    employeeName: string; jobNumber: string; date: string; shiftType: string
    tasks: RosterPushEntry['tasks']; materials: RosterPushEntry['materials']
  }> = []

  function openPushModal() {
    // Build per-job task + material cache from MOCK_CONFIGURED_TASKS
    const tasksByJob: Record<string, RosterPushEntry['tasks']>  = {}
    const matsByJob:  Record<string, RosterPushEntry['materials']> = {}
    for (const ct of MOCK_CONFIGURED_TASKS) {
      if (!ct.pushedToMobile) continue
      if (!tasksByJob[ct.jobId]) tasksByJob[ct.jobId] = []
      tasksByJob[ct.jobId].push({ taskId: ct.id, title: ct.title, specClauseRef: ct.specClauseRef ?? null, holdPointRef: ct.holdPointLinkage ?? null, discipline: ct.discipline })
      if (ct.materialsRequired) {
        if (!matsByJob[ct.jobId]) matsByJob[ct.jobId] = []
        ct.materialsRequired.forEach(m => {
          if (!matsByJob[ct.jobId].find(x => x.bomLineId === m.bomLineId)) {
            matsByJob[ct.jobId].push({ bomLineId: m.bomLineId, description: m.description, qty: m.qty, unit: m.unit, stockStatus: getEffectiveBOMStockStatus(m.bomLineId, m.stockStatus) })
          }
        })
      }
    }

    const preview = weekDates.flatMap(date =>
      allocations
        .filter(a => a.date === date && (shiftView === 'BOTH' || a.shiftType === shiftView))
        .map(a => {
          const emp = MOCK_EMPLOYEES.find(e => e.id === a.employeeId)
          const job = MOCK_JOBS.find(j => j.id === a.jobId)
          return {
            employeeName: emp?.fullName ?? a.employeeId,
            jobNumber: job?.jobNumber ?? a.jobId,
            date, shiftType: a.shiftType,
            tasks: tasksByJob[a.jobId] ?? [],
            materials: matsByJob[a.jobId] ?? [],
          }
        })
    )
    if (preview.length === 0) { alert('No allocations to push for the selected week / shift view.'); return }
    setPushModal(preview)
  }

  function pushWeekToMobile(customNote: string) {
    // Build per-job task + material cache
    const tasksByJob: Record<string, RosterPushEntry['tasks']>  = {}
    const matsByJob:  Record<string, RosterPushEntry['materials']> = {}
    for (const ct of MOCK_CONFIGURED_TASKS) {
      if (!ct.pushedToMobile) continue
      if (!tasksByJob[ct.jobId]) tasksByJob[ct.jobId] = []
      tasksByJob[ct.jobId].push({ taskId: ct.id, title: ct.title, specClauseRef: ct.specClauseRef ?? null, holdPointRef: ct.holdPointLinkage ?? null, discipline: ct.discipline })
      if (ct.materialsRequired) {
        if (!matsByJob[ct.jobId]) matsByJob[ct.jobId] = []
        ct.materialsRequired.forEach(m => {
          if (!matsByJob[ct.jobId].find(x => x.bomLineId === m.bomLineId)) {
            matsByJob[ct.jobId].push({ bomLineId: m.bomLineId, description: m.description, qty: m.qty, unit: m.unit, stockStatus: getEffectiveBOMStockStatus(m.bomLineId, m.stockStatus) })
          }
        })
      }
    }

    const entries: RosterPushEntry[] = weekDates.flatMap(date =>
      allocations
        .filter(a => a.date === date && (shiftView === 'BOTH' || a.shiftType === shiftView))
        .map(a => {
          const emp = MOCK_EMPLOYEES.find(e => e.id === a.employeeId)
          const job = MOCK_JOBS.find(j => j.id === a.jobId)
          return {
            id: `push_${a.id}_${Date.now()}`,
            employeeId: a.employeeId, employeeName: emp?.fullName ?? a.employeeId,
            jobId: a.jobId, jobNumber: job?.jobNumber ?? a.jobId,
            date, shiftType: a.shiftType,
            pushedAt: new Date().toISOString(),
            pushedByName: user?.fullName ?? 'Unknown',
            status: 'PENDING' as const,
            confirmedAt: null,
            tasks: tasksByJob[a.jobId] ?? [],
            materials: matsByJob[a.jobId] ?? [],
            customNote: customNote || null,
          }
        })
    )
    addRosterPush(entries)
    setPushModal(null)
  }
  function doRotate(id: string, from: 'DAY'|'NIGHT') { setShiftPrefs(p=>({...p,[id]:from==='DAY'?'NIGHT':'DAY'})); setRotateConfirm(null) }

  // Filter employees and equipment if job-scoped
  // E3: Trade filter state
  const [tradeFilter, setTradeFilter] = useState('ALL')
  const tradeOptions = ['ALL', ...new Set(MOCK_EMPLOYEES.map((e: any) => e.trade ?? 'OTHER').filter(Boolean))]

  const visibleEmployees = MOCK_EMPLOYEES.filter((e: any) => {
    if (jIds === null) return true
    // Show employees that have any allocation in a scoped job OR are available
    return true  // PC can see all employees to allocate them
  })

  // Show conflicts
  const conflictCells = allocations.filter(a => {
    const onDate = allocations.filter(x => x.employeeId === a.employeeId && x.date === a.date)
    return onDate.length > 1
  })

  const handleRemoveAlloc = (id: string) => {
    const removing = allocations.find(a => a.id === id)
    setAllocations(prev => prev.filter(a => a.id !== id))
    // R4: Log the removal
    if (removing) {
      const removedJob = MOCK_JOBS.find(j => j.id === removing.jobId)
      addRosterChangeEntry({
        action: 'REMOVE', employeeId: removing.employeeId,
        employeeName: MOCK_EMPLOYEES.find(e => e.id === removing.employeeId)?.fullName ?? removing.employeeId,
        date: removing.date, jobId: removing.jobId, jobNumber: removedJob?.jobNumber ?? null,
        shiftType: removing.shiftType, changedByName: user?.fullName ?? 'PM',
        previousState: JSON.stringify({ jobId: removing.jobId, shiftType: removing.shiftType }),
        newState: null, reason: 'Allocation removed', weekStarting: weekDates[0] ?? removing.date,
      })
    }
  }

  const handleSaveAlloc = (data: Omit<DayAllocation, 'id'>) => {
    const newId = `al_${Date.now()}`
    setAllocations(prev => [...prev, { ...data, id: newId }])
    // R4: Log the addition
    const addedJob = MOCK_JOBS.find(j => j.id === data.jobId)
    addRosterChangeEntry({
      action: 'ADD', employeeId: data.employeeId,
      employeeName: MOCK_EMPLOYEES.find(e => e.id === data.employeeId)?.fullName ?? data.employeeId,
      date: data.date, jobId: data.jobId, jobNumber: addedJob?.jobNumber ?? null,
      shiftType: data.shiftType, changedByName: data.allocatedByName ?? user?.fullName ?? 'PM',
      previousState: null, newState: JSON.stringify({ jobId: data.jobId, shiftType: data.shiftType }),
      reason: data.notes ?? 'Allocation added', weekStarting: weekDates[0] ?? data.date,
    })
    // Persist to Neon for real employees and jobs (fire-and-forget)
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (UUID_RE.test(data.employeeId) && UUID_RE.test(data.jobId)) {
      api.post('/api/roster/allocations', {
        employeeId:    data.employeeId,
        jobId:         data.jobId,
        allocationDate: data.date,
        shift:         data.shiftType,
        notes:         data.notes ?? null,
        createdById:   data.allocatedById ?? null,
      }).catch((err: unknown) => console.warn('Roster Neon write failed:', err))
    }
  }

  const handleRemoveEqAlloc = (id: string) => {
    setEqAllocations(prev => prev.filter(a => a.id !== id))
  }

  const handleSaveEqAlloc = (equipmentId: string, date: string) => {
    const availJobs = jIds === null ? MOCK_JOBS : MOCK_JOBS.filter(j => jIds.includes(j.id))
    if (availJobs.length === 0) return
    const conflict = checkEquipmentConflict(equipmentId, [date], undefined, eqAllocations)
    if (conflict.hasConflict) { alert(`Conflict: equipment already allocated to ${conflict.conflictJobId} on ${date}`); return }
    setEqAllocations(prev => [...prev, {
      id: `ea_${Date.now()}`, equipmentId, jobId: availJobs[0].id, date,
      allocatedById: user.id, allocatedByName: user.fullName, allocatedAt: new Date().toISOString(), notes: null,
    }])
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crew Roster</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {jIds !== null
              ? `Viewing ${jIds.length} assigned job${jIds.length !== 1 ? 's' : ''} — ${weekLabel}`
              : `All projects — ${weekLabel}`
            }
            {conflictCells.length > 0 && <span className="ml-2 text-red-600 font-medium">· {conflictCells.length} conflict{conflictCells.length !== 1 ? 's' : ''} detected</span>}
            {liveAllocCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {liveAllocCount} live from Neon</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <div className="flex items-center gap-0.5 rounded-lg p-0.5 bg-gray-100">
            {([['DAY','Day Mon–Fri'],['NIGHT','Night Sun–Thu'],['BOTH','Both']] as const).map(([key,label]) => (
              <button key={key} onClick={() => setShiftView(key)} title={label}
                className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${shiftView===key ? key==='NIGHT' ? 'bg-indigo-900 text-amber-300 shadow-sm' : 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {key==='DAY'&&<Sun className="w-3 h-3"/>}{key==='NIGHT'&&<Moon className="w-3 h-3"/>}{key}
              </button>
            ))}
          </div>
          {/* Span selector */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
            {([1,2,3,4] as const).map(n => (
              <button key={n} onClick={() => setWeekSpan(n)}
                className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                  weekSpan === n ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                {n === 4 ? '1 month' : `${n}W`}
              </button>
            ))}
          </div>
          {/* Week navigator */}
          <div className="flex items-center gap-1">
            <button onClick={() => setWeekOffset(w => w - weekSpan)} className="btn-secondary p-2"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setWeekOffset(0)} className="btn-secondary text-xs px-3 py-2">Today</button>
            <button onClick={() => setWeekOffset(w => w + weekSpan)} className="btn-secondary p-2"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Conflict banner */}
      {conflictCells.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800 font-medium">
            {conflictCells.length} allocation conflict{conflictCells.length !== 1 ? 's' : ''} detected — employee assigned to multiple jobs on the same day. Review and resolve.
          </p>
        </div>
      )}

      {/* PC job scope info */}
      {jIds !== null && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <Info className="w-4 h-4 text-sky-600 flex-shrink-0" />
          <p className="text-sm text-sky-800">
            You can <strong>add and remove allocations</strong> for your assigned jobs ({jIds.join(', ')}).
            Allocations for other jobs are shown <span className="opacity-70">greyed</span> and are <strong>read-only</strong>.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('crew')}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab === 'crew' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Users className="w-4 h-4" />Crew roster
        </button>
        <button onClick={() => setTab('equipment')}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab === 'equipment' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Truck className="w-4 h-4" />Equipment schedule
        </button>
        <button onClick={() => setTab('payroll')}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab === 'payroll' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <DollarSign className="w-4 h-4" />Payroll summary
        </button>
        <button onClick={() => setTab('history')}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <FileText className="w-4 h-4" />Change log ({getRosterChangeLog(weekDates[0]).length})
        </button>
      </div>

      {/* ── CREW ROSTER TAB ──────────────────────────────────────── */}
      {tab === 'crew' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Legend */}
          <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap text-xs">
              {MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j => {
                const c = JOB_ALLOC_COLOUR[j.id]
                if (!c) return null
                const inScope = jIds === null || jIds.includes(j.id)
                return (
                  <div key={j.id} className={`flex items-center gap-1.5 ${inScope ? '' : 'opacity-50'}`}>
                    <div className={`w-2.5 h-2.5 rounded-sm ${c.bg} border ${c.border}`} />
                    <span className="text-gray-600">{j.jobNumber}</span>
                  </div>
                )
              })}
              <div className="flex items-center gap-1.5 text-gray-400">
                <div className="w-2.5 h-2.5 rounded-sm bg-gray-100 border border-gray-300" />
                <span>Available</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {shiftView==='NIGHT'&&<span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1"><Moon className="w-3 h-3"/>Night view — Sun–Thu · NIGHT allocations only</span>}
              {shiftView==='BOTH'&&<span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">Both shifts — DAY + NIGHT cells visible</span>}
              {canEdit&&<div className="text-xs text-gray-400">Click empty cell to allocate · hover to remove</div>}
            </div>
          </div>

          {/* E3: Trade filter */}
          <div className="flex gap-1.5 flex-wrap mb-3">
            {tradeOptions.map((t: string) => (
              <button key={t} onClick={() => setTradeFilter(t)}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${tradeFilter === t ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {t === 'ALL' ? 'All trades' : t}
              </button>
            ))}
            {tradeFilter !== 'ALL' && (
              <span className="text-xs text-gray-400 self-center">{visibleEmployees.length} shown</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-700 w-44 sticky left-0 bg-gray-50">Employee</th>
                  {weekDates.map((date) => {
                    const d = new Date(date + 'T00:00:00')
                    const dayLabel = d.toLocaleDateString('en-AU', { weekday: 'short' })
                    const isMonday = d.getDay() === 1
                    return (
                      <th key={date} className={`text-center px-2 py-2.5 text-xs font-semibold min-w-[7rem] ${
                        d.getDay() === 6 && shiftView !== 'NIGHT' ? 'text-gray-300 bg-gray-50/60' : // Sat — weekend (DAY view)
                        d.getDay() === 0 ? 'text-amber-700 bg-amber-50/80' :              // Sun — night shift start
                        d.getDay() === 5 && shiftView === 'DAY'   ? 'text-sky-700 bg-sky-50/60' :  // Fri — day crew end
                        d.getDay() === 5 && shiftView === 'NIGHT' ? 'text-gray-400 bg-gray-50' :   // Fri — off for nights
                        d.getDay() === 4 && shiftView === 'NIGHT' ? 'text-amber-600 bg-amber-50/60' : // Thu — last night
                        shiftView === 'NIGHT' ? 'text-indigo-700 bg-indigo-50/40' :       // Mon-Wed night zone
                        weekSpan > 1 && d.getDay() === 1 ? 'text-gray-700 border-l-2 border-blue-100' : // Mon divider
                        'text-gray-700'
                      }`}>
                        <div>{dayLabel}</div>
                        <div className="text-gray-400 font-normal">{formatShortDate(date)}</div>
                      </th>
                    )
                  })}
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-600 w-16">Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleEmployees.map(emp => {
                  const avail = getEmployeeWeekAvailability(emp.id, weekDates, allocations)
                  const dayCount = avail.filter(a => a.status === 'ALLOCATED').length
                  const hasConflict = allocations.filter(a => a.employeeId === emp.id && weekDates.includes(a.date)).some(a => {
                    return allocations.filter(x => x.employeeId === emp.id && x.date === a.date).length > 1
                  })
                  // I1: FM status badge on employee row
                  const empFmStatus = getEmployeeFMStatus(emp.id, allocations)
                  const isApprenticeRow = (emp as any).employmentType === 'APPRENTICE' || (emp as any).employmentType === 'HOST_APPRENTICE'

                  return (
                    <tr key={emp.id} className={`hover:bg-gray-50/50 ${hasConflict ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-2.5 sticky left-0 bg-white border-r border-gray-100">
                        <div className="flex items-center gap-2">
                          {empFmStatus !== 'CLEAR' && (
                            <span className={`flex-shrink-0 text-[10px] font-bold px-1 py-0.5 rounded ${empFmStatus === 'RED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}
                              title="Fatigue management flag">FM</span>
                          )}
                          {isApprenticeRow && (
                            <span className="flex-shrink-0 text-[10px] font-bold px-1 py-0.5 rounded bg-teal-100 text-teal-700" title="Apprentice — supervision required">AP</span>
                          )}
                          {/* E3: Trade badge */}
                          {(emp as any).trade && (emp as any).trade !== 'MANAGEMENT' && (
                            <span className="flex-shrink-0 text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500" title={(emp as any).trade}>
                              {((emp as any).trade as string).slice(0,4)}
                            </span>
                          )}
                          {hasConflict && <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">{emp.fullName}</div>
                            <div className="flex items-center gap-1 mt-0.5">
                              {empShift(emp.id)==='NIGHT'
                                ?<span className="inline-flex items-center gap-0.5 text-xs bg-indigo-900 text-amber-300 px-1.5 py-0.5 rounded-full font-medium"><Moon className="w-2.5 h-2.5"/>Night</span>
                                :<span className="inline-flex items-center gap-0.5 text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full font-medium"><Sun className="w-2.5 h-2.5"/>Day</span>}
                              {canEdit&&<button onClick={()=>setRotateConfirm({id:emp.id,name:emp.fullName,from:empShift(emp.id)})} title="Rotate shift" className="ml-0.5 text-gray-300 hover:text-indigo-500"><RefreshCw className="w-3 h-3"/></button>}
                            </div>
                          </div>
                        </div>
                      </td>
                      {weekDates.map(date => (
                        <AllocCell key={date}
                          employeeId={emp.id} date={date}
                          allocations={allocations}
                          canEdit={canEdit}
                          scopedJobIds={jIds}
                          shiftFilter={shiftView}
                          override={getOverride(emp.id, date)}
                          onEditStatus={(eId, d, aId) => setEditStatusModal({ employeeId: eId, date: d, allocationId: aId })}
                          onAllocate={(eId, d) => setAllocModal({ employeeId: eId, date: d })}
                          onRemove={handleRemoveAlloc}
                        />
                      ))}
                      <td className="px-4 py-2.5 text-right">
                        <div className={`text-sm font-bold ${dayCount >= 5 ? 'text-amber-600' : dayCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                          {dayCount}
                        </div>
                        <div className="text-xs text-gray-400">/5</div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Footer summary */}
          <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
            <div>{visibleEmployees.length} employees · {weekSpan === 1 ? "1 week" : weekSpan === 4 ? "1 month" : `${weekSpan} weeks`} view</div>
            <div>
              Total allocations: <strong>{allocations.filter(a => weekDates.includes(a.date)).length}</strong> employee-days
            </div>
          </div>
        </div>
      )}

      {/* ── EQUIPMENT TAB ─────────────────────────────────────── */}
      {tab === 'equipment' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900 text-sm">Equipment & vehicle schedule</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-700 w-52 sticky left-0 bg-gray-50">Equipment</th>
                  {weekDates.map((date) => {
                    const d = new Date(date + 'T00:00:00')
                    const dayLabel = d.toLocaleDateString('en-AU', { weekday: 'short' })
                    const isMonday = d.getDay() === 1
                    return (
                      <th key={date} className={`text-center px-2 py-2.5 text-xs font-semibold min-w-[7rem] ${
                        d.getDay() === 6 && shiftView !== 'NIGHT' ? 'text-gray-300 bg-gray-50/60' : // Sat — weekend (DAY view)
                        d.getDay() === 0 ? 'text-amber-700 bg-amber-50/80' :              // Sun — night shift start
                        d.getDay() === 5 && shiftView === 'DAY'   ? 'text-sky-700 bg-sky-50/60' :  // Fri — day crew end
                        d.getDay() === 5 && shiftView === 'NIGHT' ? 'text-gray-400 bg-gray-50' :   // Fri — off for nights
                        d.getDay() === 4 && shiftView === 'NIGHT' ? 'text-amber-600 bg-amber-50/60' : // Thu — last night
                        shiftView === 'NIGHT' ? 'text-indigo-700 bg-indigo-50/40' :       // Mon-Wed night zone
                        weekSpan > 1 && d.getDay() === 1 ? 'text-gray-700 border-l-2 border-blue-100' : // Mon divider
                        'text-gray-700'
                      }`}>
                        <div>{dayLabel}</div>
                        <div className="text-gray-400 font-normal">{formatShortDate(date)}</div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {MOCK_EQUIPMENT.slice(0, 8).map(eq => (
                  <tr key={eq.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 sticky left-0 bg-white border-r border-gray-100">
                      <div className="text-sm font-medium text-gray-900">{eq.name}</div>
                      <div className="text-xs text-gray-400">{eq.assetNumber} · {eq.category}</div>
                    </td>
                    {weekDates.map(date => (
                      <EquipAllocCell key={date}
                        equipmentId={eq.id} date={date}
                        allocations={eqAllocations}
                        canEdit={canEdit}
                        onAllocate={handleSaveEqAlloc}
                        onRemove={handleRemoveEqAlloc}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PAYROLL TAB ───────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Roster change history</h3>
                <p className="text-xs text-gray-500 mt-0.5">Week commencing {weekDates[0]} — all allocation additions, removals, and push events</p>
              </div>
            </div>
            {getRosterChangeLog(weekDates[0]).length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-400 text-sm">
                No roster changes logged for this week yet. Changes are recorded when allocations are added or removed.
              </div>
            ) : (
              <div className="divide-y">
                {[...getRosterChangeLog(weekDates[0])].reverse().map(entry => (
                  <div key={entry.id} className="px-5 py-3.5 flex items-start gap-4">
                    <div className={`flex-shrink-0 mt-0.5 w-2 h-2 rounded-full ${entry.action === 'ADD' ? 'bg-green-500' : entry.action === 'REMOVE' ? 'bg-red-500' : entry.action === 'PUSH' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-900">{entry.employeeName}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${entry.action === 'ADD' ? 'bg-green-100 text-green-700' : entry.action === 'REMOVE' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                          {entry.action}
                        </span>
                        {entry.jobNumber && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{entry.jobNumber}</span>}
                        <span className="text-xs text-gray-500">{entry.date} · {entry.shiftType}</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {entry.reason} · by {entry.changedByName} · {new Date(entry.changedAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'})}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'payroll' && (
        isTier('B_PM')
          ? <PayrollTab weekDates={weekDates} allocations={allocations} />
          : <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-400">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Payroll summary is available to Project Managers and above.</p>
            </div>
      )}


      {/* ── Status / Hours edit modal ───────────────────────────── */}
      {editStatusModal && (() => {
        const emp = MOCK_EMPLOYEES.find(e => e.id === editStatusModal.employeeId)
        const existing = getOverride(editStatusModal.employeeId, editStatusModal.date)
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="px-5 py-4 border-b bg-gray-50 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">Edit day status / hours</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {emp?.fullName} · {new Date(editStatusModal.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </p>
                </div>
                <button onClick={() => setEditStatusModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <StatusEditForm
                existing={existing}
                onSave={(status, hours, note) => {
                  applyStatus(editStatusModal.employeeId, emp?.fullName ?? '', editStatusModal.date, editStatusModal.allocationId, status, hours, note)
                  setEditStatusModal(null)
                }}
                onCancel={() => setEditStatusModal(null)}
              />
            </div>
          </div>
        )
      })()}

      {/* Shift rotation confirmation modal */}
      {rotateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-5 py-4 border-b bg-amber-50 flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold text-gray-900">Rotate shift crew</h2>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700">
                Rotate <strong>{rotateConfirm.name}</strong> from{' '}
                {rotateConfirm.from === 'DAY'
                  ? <><span className="inline-flex items-center gap-0.5 text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full font-medium"><Sun className="w-2.5 h-2.5" />Day shift</span>{' '}to{' '}<span className="inline-flex items-center gap-0.5 text-xs bg-indigo-900 text-amber-300 px-1.5 py-0.5 rounded-full font-medium"><Moon className="w-2.5 h-2.5" />Night shift</span></>
                  : <><span className="inline-flex items-center gap-0.5 text-xs bg-indigo-900 text-amber-300 px-1.5 py-0.5 rounded-full font-medium"><Moon className="w-2.5 h-2.5" />Night shift</span>{' '}to{' '}<span className="inline-flex items-center gap-0.5 text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full font-medium"><Sun className="w-2.5 h-2.5" />Day shift</span></>
                }?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                {rotateConfirm.from === 'DAY'
                  ? 'Night shift: Sunday evening start — Thursday evening last shift. Ensure accommodation is booked if the site is away from depot.'
                  : 'Day shift: Monday to Friday. Ensure night shift coverage is arranged before removing this crew member from nights.'}
              </div>
              <p className="text-xs text-gray-400">Existing allocations are not changed — only future scheduling reflects the new shift assignment.</p>
            </div>
            <div className="px-5 pb-4 flex gap-3">
              <button onClick={() => setRotateConfirm(null)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => doRotate(rotateConfirm.id, rotateConfirm.from)}
                className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold">
                Confirm rotation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Allocation modal */}
      {allocModal && (
        <AllocateModal
          employeeId={allocModal.employeeId} date={allocModal.date}
          allocations={allocations} scopedJobIds={jIds}
          currentUser={{ id: user.id, fullName: user.fullName }}
          onSave={handleSaveAlloc}
          onClose={() => setAllocModal(null)}
        />
      )}

      {/* Push details modal */}
      {pushModal && (
        <PushDetailsModal
          entries={pushModal}
          onConfirm={(note) => pushWeekToMobile(note)}
          onClose={() => setPushModal(null)}
        />
      )}
    </div>
  )
}
